import React, { useEffect, useState, Children } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { validateLoginForm } from '../utils/validation';
import { Sparkles, CheckCircle2, Star, Users } from 'lucide-react';
import { motion } from 'framer-motion';
import { resetRateLimit } from '../utils/security';
export function LoginPage() {
  const {
    login
  } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  // Clear any rate limit on page load so user can try again
  useEffect(() => {
    resetRateLimit();
  }, []);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');
    const validation = validateLoginForm(email, password);
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }
    setErrors({});
    setIsLoading(true);
    const result = await login(email, password);
    if (!result.success) {
      setLoginError(result.error || 'Error al iniciar sesión');
    }
    setIsLoading(false);
  };
  // Animation variants
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };
  const itemVariants = {
    hidden: {
      y: 20,
      opacity: 0
    },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 10
      }
    }
  };
  const floatingVariant = {
    animate: {
      y: [0, -10, 0],
      transition: {
        duration: 3,
        repeat: Infinity,
        ease: 'easeInOut'
      }
    }
  };
  return <div className="min-h-screen w-full flex overflow-hidden bg-gray-50 dark:bg-gray-900" data-id="element-2403">
      {/* Left Decorative Panel */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-teal-900 overflow-hidden items-center justify-center" data-id="element-2404">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0" data-id="element-2405">
          <img src="https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=1200&auto=format&fit=crop" alt="Spa Wellness Background" className="w-full h-full object-cover opacity-60 mix-blend-overlay" data-id="element-2406" />
          <div className="absolute inset-0 bg-gradient-to-br from-teal-900/90 via-teal-800/80 to-cyan-900/90 mix-blend-multiply" data-id="element-2407" />
        </div>

        {/* Animated Decorative Orbs */}
        <motion.div animate={{
        scale: [1, 1.2, 1],
        opacity: [0.3, 0.5, 0.3]
      }} transition={{
        duration: 8,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-teal-400/20 rounded-full blur-3xl z-10" data-id="element-2408" />
        <motion.div animate={{
        scale: [1, 1.1, 1],
        opacity: [0.2, 0.4, 0.2]
      }} transition={{
        duration: 10,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: 1
      }} className="absolute bottom-[-5%] right-[-5%] w-[500px] h-[500px] bg-cyan-400/20 rounded-full blur-3xl z-10" data-id="element-2409" />

        {/* Content Container */}
        <div className="relative z-20 max-w-lg px-12 text-center" data-id="element-2410">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-2411">
            <div className="inline-flex items-center justify-center p-3 bg-white/10 backdrop-blur-md rounded-2xl mb-8 ring-1 ring-white/20 shadow-lg" data-id="element-2412">
              <Sparkles className="w-8 h-8 text-teal-200" data-id="element-2413" />
            </div>
            <h2 className="text-5xl font-bold text-white mb-6 tracking-tight leading-tight" data-id="element-2414">
              Bienestar y Belleza <br data-id="element-2415" />
              <span className="text-teal-200" data-id="element-2416">en Armonía</span>
            </h2>
            <p className="text-lg text-teal-50/90 leading-relaxed mb-12 font-light" data-id="element-2417">
              Gestione su centro con elegancia y eficiencia. La plataforma
              integral para profesionales del bienestar.
            </p>
          </motion.div>

          {/* Floating Glass Cards */}
          <div className="grid grid-cols-3 gap-4" data-id="element-2418">
            <motion.div variants={floatingVariant} animate="animate" className="bg-white/10 backdrop-blur-md rounded-2xl p-4 border border-white/10 text-center transform hover:scale-105 transition-transform duration-300" data-id="element-2419">
              <Users className="w-6 h-6 text-teal-200 mx-auto mb-2" data-id="element-2420" />
              <div className="text-2xl font-bold text-white" data-id="element-2421">500+</div>
              <div className="text-xs text-teal-100/80 uppercase tracking-wider mt-1" data-id="element-2422">
                Clientes
              </div>
            </motion.div>

            <motion.div variants={floatingVariant} animate="animate" transition={{
            delay: 0.2
          }} className="bg-white/10 backdrop-blur-md rounded-2xl p-4 border border-white/10 text-center transform hover:scale-105 transition-transform duration-300" data-id="element-2423">
              <CheckCircle2 className="w-6 h-6 text-teal-200 mx-auto mb-2" data-id="element-2424" />
              <div className="text-2xl font-bold text-white" data-id="element-2425">50+</div>
              <div className="text-xs text-teal-100/80 uppercase tracking-wider mt-1" data-id="element-2426">
                Servicios
              </div>
            </motion.div>

            <motion.div variants={floatingVariant} animate="animate" transition={{
            delay: 0.4
          }} className="bg-white/10 backdrop-blur-md rounded-2xl p-4 border border-white/10 text-center transform hover:scale-105 transition-transform duration-300" data-id="element-2427">
              <Star className="w-6 h-6 text-teal-200 mx-auto mb-2" data-id="element-2428" />
              <div className="text-2xl font-bold text-white" data-id="element-2429">4.9</div>
              <div className="text-xs text-teal-100/80 uppercase tracking-wider mt-1" data-id="element-2430">
                Rating
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Right Login Form Panel */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center items-center p-6 lg:p-12 relative bg-white/50 dark:bg-gray-900/50" data-id="element-2431">
        {/* Mobile Background Decoration */}
        <div className="absolute inset-0 lg:hidden z-0 overflow-hidden pointer-events-none" data-id="element-2432">
          <div className="absolute top-[-20%] right-[-20%] w-96 h-96 bg-teal-200/30 rounded-full blur-3xl" data-id="element-2433" />
          <div className="absolute bottom-[-20%] left-[-20%] w-96 h-96 bg-cyan-200/30 rounded-full blur-3xl" data-id="element-2434" />
        </div>

        <motion.div variants={containerVariants} initial="hidden" animate="visible" className="w-full max-w-md relative z-10" data-id="element-2435">
          {/* Logo Section */}
          <motion.div variants={itemVariants} className="text-center mb-10" data-id="element-2436">
            <motion.div whileHover={{
            scale: 1.05
          }} transition={{
            type: 'spring',
            stiffness: 300
          }} className="inline-block relative" data-id="element-2437">
              <div className="absolute inset-0 bg-teal-500 rounded-full blur-lg opacity-20 animate-pulse" data-id="element-2438"></div>
              <img src="https://cdn.magicpatterns.com/uploads/a741bfNsYBMGGZNxniTt4s/images.jpg" alt="Savidna Logo" className="w-24 h-24 rounded-full shadow-2xl relative z-10 border-4 border-white dark:border-gray-800 object-cover" data-id="element-2439" />
            </motion.div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mt-6 mb-2 tracking-tight" data-id="element-2440">
              Savidna
            </h1>
            <p className="text-lg text-teal-600 dark:text-teal-400 font-medium" data-id="element-2441">
              Centro de Bienestar y Belleza
            </p>
          </motion.div>

          {/* Login Card */}
          <motion.div variants={itemVariants} className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl shadow-teal-900/5 p-8 lg:p-10 border border-gray-100 dark:border-gray-700 backdrop-blur-sm" data-id="element-2442">
            <div className="mb-8" data-id="element-2443">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-2444">
                Bienvenido de nuevo
              </h2>
              <p className="text-gray-500 dark:text-gray-400" data-id="element-2445">
                Ingresa tus credenciales para acceder al panel
              </p>
            </div>

            {loginError && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl flex items-start gap-3" data-id="element-2446">
                <div className="text-red-500 mt-0.5" data-id="element-2447">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-2448">
                    <circle cx="12" cy="12" r="10" data-id="element-2449" />
                    <line x1="12" x2="12" y1="8" y2="12" data-id="element-2450" />
                    <line x1="12" x2="12.01" y1="16" y2="16" data-id="element-2451" />
                  </svg>
                </div>
                <p className="text-sm text-red-600 dark:text-red-400 font-medium" data-id="element-2452">
                  {loginError}
                </p>
              </motion.div>}

            <form onSubmit={handleSubmit} className="space-y-6" data-id="element-2453">
              <div className="space-y-2" data-id="element-2454">
                <Input type="email" label="Correo Electrónico" placeholder="nombre@empresa.com" value={email} onChange={e => setEmail(e.target.value)} error={errors.email} className="h-12 bg-gray-50 dark:bg-gray-900/50 border-gray-200 dark:border-gray-700 focus:ring-teal-500 focus:border-teal-500 transition-all" data-id="element-2455" />
              </div>

              <div className="space-y-2" data-id="element-2456">
                <Input type="password" label="Contraseña" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} error={errors.password} className="h-12 bg-gray-50 dark:bg-gray-900/50 border-gray-200 dark:border-gray-700 focus:ring-teal-500 focus:border-teal-500 transition-all" data-id="element-2457" />
              </div>

              <Button type="submit" className="w-full h-12 text-lg font-medium bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-500 hover:to-cyan-500 shadow-lg shadow-teal-500/20 rounded-xl transition-all duration-300 transform hover:-translate-y-0.5" isLoading={isLoading} data-id="element-2458">
                Iniciar Sesión
              </Button>
            </form>
          </motion.div>

          {/* Footer */}
          <motion.p variants={itemVariants} className="text-center text-xs text-gray-400 dark:text-gray-500 mt-8 max-w-xs mx-auto leading-relaxed" data-id="element-2459">
            <span className="flex items-center justify-center gap-1.5 mb-2" data-id="element-2460">
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-2461">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" data-id="element-2462" />
              </svg>
              Conexión segura · Contraseñas cifradas
            </span>
            © 2026 Savidna. Sistema de gestión profesional para centros de
            bienestar.
          </motion.p>
        </motion.div>
      </div>
    </div>;
}